import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onFinish", "children", "videoId", "autoPlay", "playSpeed", "language", "onProgress", "onPlay", "onTimeUpdate", "videoDuration", "currentTime", "completedVideoView", "videoType"],
  _excluded2 = ["contentId", "lessonId", "trackId", "language"],
  _excluded3 = ["shortId", "shortName", "playlistId"],
  _excluded4 = ["shortId"];
import enviro from 'enviro';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import omit from 'hs-lodash/omit';
import Raven from 'raven-js';
import getLang from 'LearningCenterUI/utils/getLang';
import { isAcceptanceTest } from 'hub-http/helpers/env';
import { na1 } from 'hubspot-url-utils/hublets';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import styled from 'foundations-theming/utils';
import { RotateIcon } from 'foundations-assets/icons/components/RotateIcon';
import { Flex } from 'foundations-components/components/Flex';
import { TrackingContentTypes } from '../../../constants/Tracking';
import ContentViewport from '../../../components/ContentViewport';
import { useVideo } from '../../../features/video/videoQueries';
import { Metrics } from '../../../Metrics';
import { FILTER_TYPES } from '../../../constants/TopFilterConstants';
// @ts-expect-error Could not find a declaration file for module// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../../../hooks/searchParamHooks';
import { useShort, useStartShort } from '../../shortContent/shortContentQueries';
import { useShortVideo } from '../../shortContent/shortContentHooks';
import { EmbeddedVideo } from './EmbeddedVideo';
import { useCurrentVideoTime } from './VideoPlayerWithControls';
import { useAutoClearVideoVTS, useVideoParams, useVideoPlayerConfig, useVideoTimestamp } from '../videoHooks';
import { convertToMinutes } from '../../../utils/utils';
import UsageTracker from '../../../utils/UsageTracker';
import { usePostRecommendationsContentEngagedFeedback } from '../../recommendations/recommendationsQueries';
import { usePlaylistIdFromSearch } from '../../playlistContent/hooks';
import { usePlaylist } from '../../savedItems/savedItemsQueries';
import { useIsHubspotPlaylist } from '../../savedItems/savedItemsHooks';
import { updateContentProgress } from '../../userProgress/userProgressQueries';
import { useLearnByDoing } from '../../toolRecommendations';
import { LearnByDoingCTAScreen } from '../../toolRecommendations';
import { useSearchState } from '../../search/searchStore';
import { useRequestLearnWhileDoing } from '../../toolRecommendations';
import PracticeModeOverlay from '../../practiceMode/components/PracticeModeOverlay';
import { useIsPracticeModeEnabled } from '../../practiceMode/practiceModeHooks';
import { SHORTS_MOCK_TEST_THUMBNAIL } from '../videoConstants';

// @bend-expect trellis-tokenized
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ReplayIconWrapper = styled.div.withConfig({
  displayName: "VideoPlayer__ReplayIconWrapper"
})(({
  theme
}) => ({
  color: theme.color['icon-core-default'],
  position: 'absolute',
  cursor: 'pointer',
  bottom: theme.space['300'],
  left: theme.space['300'],
  fontSize: '22px',
  transform: 'scaleX(-1)'
}));
const VideoPlayerContext = /*#__PURE__*/createContext({
  isShowingCompletedScreen: false,
  showCompletedScreen: () => {},
  hideCompletedScreen: () => {}
});
export const useVideoPlayerContext = () => useContext(VideoPlayerContext);
export const VideoPlayerProvider = ({
  children
}) => {
  const [isShowingCompletedScreen, setShowCompletedVideoScreen] = useState(false);
  const showCompletedScreen = useCallback(() => setShowCompletedVideoScreen(true), [setShowCompletedVideoScreen]);
  const hideCompletedScreen = useCallback(() => setShowCompletedVideoScreen(false), [setShowCompletedVideoScreen]);
  const contextState = {
    isShowingCompletedScreen,
    showCompletedScreen,
    hideCompletedScreen
  };
  return /*#__PURE__*/_jsx(VideoPlayerContext.Provider, {
    value: contextState,
    children: children
  });
};
const getStartTime = ({
  startTimeFromUrl,
  startTimeFromProps,
  videoDuration
}) => {
  const desiredTime = startTimeFromProps || startTimeFromUrl;
  if (desiredTime && videoDuration && desiredTime < videoDuration) return desiredTime;
  return undefined;
};
const VideoPlayerBase = _ref => {
  let {
      onFinish,
      children,
      videoId,
      autoPlay,
      playSpeed,
      language,
      onProgress,
      onPlay,
      onTimeUpdate,
      videoDuration,
      currentTime: startTimeFromProps,
      completedVideoView,
      videoType
    } = _ref,
    itemProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    clearLastClickedSearchResultItem,
    lastClickedSearchResultItem
  } = useSearchState();
  const playlistId = usePlaylistIdFromSearch();
  const {
    playlist
  } = usePlaylist(playlistId, !playlistId);
  const isHubspotPlaylist = useIsHubspotPlaylist(playlist);
  const {
    setHasVideoPlayed,
    setIsVideoFinished
  } = useCurrentVideoTime();
  useAutoClearVideoVTS();
  const {
    subtitleLanguage
  } = useVideoPlayerConfig();
  const secondsElapsed = useRef(0);
  const [hasClickedPlayForVideo, setHasClickedPlayForVideo] = useState(false);
  const hasMarkedProgress = useRef(false);
  const {
    startTime: startTimeFromUrl
  } = useVideoParams();
  const {
    videoTimestamp
  } = useVideoTimestamp();
  const {
    isShowingCompletedScreen,
    showCompletedScreen,
    hideCompletedScreen
  } = useVideoPlayerContext();
  const {
    isVideoFinished,
    handleEnableLearnWhileDoingScreen,
    handleVideoPlayed,
    handleVideoFinished
  } = useRequestLearnWhileDoing();

  // Capture initial global timestamp on mount to detect stale data
  const initialTimestamp = useRef(videoTimestamp);

  // Ignore stale timestamp from previous video (if unchanged since mount)
  const activeTimestamp = videoTimestamp === initialTimestamp.current ? undefined : videoTimestamp;
  const startTime = getStartTime({
    startTimeFromUrl,
    startTimeFromProps,
    videoDuration: videoDuration
  });
  const buildWhatExtraJson = useCallback((action, props) => {
    const itemVisibility = itemProps.visibility ? readUnhandledEnumCase(itemProps.visibility) : undefined;
    return Object.assign({
      action,
      contentLanguage: language,
      contentType: TrackingContentTypes.VIDEO,
      videoType,
      visibility: itemVisibility
    }, playlistId && {
      playlistId
    }, playlist && {
      isHubspotPlaylist
    }, playlist && {
      playlistName: playlist.name
    }, props, omit(itemProps, 'PlayerComponent', 'onPlay', 'visibility'));
  }, [language, videoType, itemProps, playlist, playlistId, isHubspotPlaylist]);
  const handleVideoPlay = useCallback(() => {
    setHasVideoPlayed(true);
    if (!hasClickedPlayForVideo) {
      let extraTrackingInfo;
      // extra tracking for search results, to be removed once searchv1 is gone
      if (lastClickedSearchResultItem) {
        const id = Number(itemProps.trackId || itemProps.lessonId || itemProps.shortId);
        const type = itemProps.trackId ? 'TRACK' : itemProps.lessonId ? 'LESSON' : 'SHORT_FORM_CONTENT';
        if (id === lastClickedSearchResultItem.item.id && type === lastClickedSearchResultItem.item.type) {
          extraTrackingInfo = {
            position: lastClickedSearchResultItem.position
          };
          clearLastClickedSearchResultItem();
        }
      }
      setHasClickedPlayForVideo(true);
      if (onPlay) {
        onPlay();
      }
      UsageTracker.track('contentInteraction', Object.assign({}, buildWhatExtraJson('video-play'), extraTrackingInfo));
    }
    Metrics.counter('hs-video-played').increment();
  }, [buildWhatExtraJson, clearLastClickedSearchResultItem, hasClickedPlayForVideo, itemProps.lessonId, itemProps.shortId, itemProps.trackId, lastClickedSearchResultItem, onPlay, setHasVideoPlayed]);
  const handleUpdateProgress = useCallback(props => {
    UsageTracker.track('contentProgress', buildWhatExtraJson('video-progression', props));
    if (onProgress) onProgress();
  }, [buildWhatExtraJson, onProgress]);
  const handleVideoFinish = useCallback(() => {
    setIsVideoFinished(true);
    UsageTracker.track('contentInteraction', buildWhatExtraJson('video-finished'));
    Metrics.counter('hs-video-finished').increment();
    const viewDuration = videoDuration && convertToMinutes(videoDuration - secondsElapsed.current);
    if (onFinish) {
      onFinish();
      if (!hasMarkedProgress && viewDuration) handleUpdateProgress({
        viewDuration
      });
    }
    showCompletedScreen();
  }, [buildWhatExtraJson, handleUpdateProgress, hasMarkedProgress, onFinish, showCompletedScreen, videoDuration, setIsVideoFinished]);
  useEffect(() => {
    if (videoId) {
      hasMarkedProgress.current = false;
      // Reset play state when video changes
      setHasVideoPlayed(false);
      setIsVideoFinished(false);
    }
    setHasClickedPlayForVideo(false);
    hideCompletedScreen();
  }, [videoId, hideCompletedScreen, setHasVideoPlayed, setIsVideoFinished]);
  const handleReplayClick = () => {
    hideCompletedScreen();
    if (itemProps.shortId) {
      handleVideoPlayed(false);
      handleVideoFinished(false);
      handleEnableLearnWhileDoingScreen();
    }
  };
  const shouldRenderCompletedVideoView = completedVideoView && isShowingCompletedScreen || isVideoFinished;
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "embedded-video",
    children: [/*#__PURE__*/_jsxs(ContentViewport, {
      viewportStyles: {
        position: 'relative'
      },
      children: [/*#__PURE__*/_jsx(EmbeddedVideo, Object.assign({
        autoPlay: autoPlay,
        language: language,
        onActivation: handleUpdateProgress,
        onFinish: handleVideoFinish,
        onPlay: handleVideoPlay,
        onTimeUpdate: onTimeUpdate,
        playSpeed: playSpeed,
        renderContext: "academy",
        startTime: startTime !== null && startTime !== void 0 ? startTime : activeTimestamp,
        subtitleLanguage: subtitleLanguage,
        videoType: videoType
      }, itemProps)), shouldRenderCompletedVideoView && /*#__PURE__*/_jsxs(Flex, {
        backgroundColor: "fill-tertiary-pressed",
        blockSize: "100%",
        inlineSize: "100%",
        insetBlockStart: "0",
        position: "absolute",
        children: [/*#__PURE__*/_jsx(ReplayIconWrapper, {
          onClick: handleReplayClick,
          children: /*#__PURE__*/_jsx(RotateIcon, {})
        }), completedVideoView]
      })]
    }), children]
  });
};
const VideoPlayerTrackLesson = _ref2 => {
  let {
      contentId,
      lessonId,
      trackId,
      language: languageProp
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    postRecommendationsContentEngagedFeedback
  } = usePostRecommendationsContentEngagedFeedback();
  const [[searchParamLanguage]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const language = languageProp || (searchParamLanguage || getLang()).toLowerCase();
  const handleOnProgress = useCallback(() => {
    if (language) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      updateContentProgress({
        contentId: Number(contentId),
        lessonId: Number(lessonId),
        trackId: Number(trackId),
        language
      });
    }
  }, [contentId, language, lessonId, trackId]);
  const handleOnPlay = useCallback(() => {
    var _postRecommendationsC;
    if ((trackId || lessonId) && contentId) (_postRecommendationsC = postRecommendationsContentEngagedFeedback({
      item: {
        id: Number(trackId || lessonId),
        type: trackId ? 'TRACK' : 'LESSON'
      },
      contentId: Number(contentId)
    })) === null || _postRecommendationsC === void 0 || _postRecommendationsC.catch(Raven.captureException.bind(Raven));
  }, [contentId, lessonId, postRecommendationsContentEngagedFeedback, trackId]);
  const videoProps = useVideo({
    contentId,
    lessonId,
    trackId,
    language
  });
  return /*#__PURE__*/_jsx(VideoPlayerBase, Object.assign({
    contentId: contentId,
    language: language,
    lessonId: lessonId,
    onPlay: handleOnPlay,
    onProgress: handleOnProgress,
    trackId: trackId
  }, props, videoProps), contentId);
};
const VideoPlayerShort = _ref3 => {
  let {
      shortId,
      shortName,
      playlistId
    } = _ref3,
    props = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  const {
    isVideoFinished
  } = useRequestLearnWhileDoing();
  const videoProps = useShortVideo({
    shortId: shortId,
    skip: !shortId
  });
  const autoplay = !!(videoProps !== null && videoProps !== void 0 && videoProps.autoPlay);
  const {
    shouldRenderLearnByDoingScreen,
    updatedVideoProps
  } = useLearnByDoing();
  const [forceAutoPlayForPracticeMode, setForceAutoPlayForPracticeMode] = useState(false);
  const shouldAutoPlayFromLearnByDoing = updatedVideoProps.autoPlay;
  const shouldAutoPlay = !shouldRenderLearnByDoingScreen ? autoplay : shouldAutoPlayFromLearnByDoing;
  const {
    short,
    shortLoading
  } = useShort(shortId, !shortId);
  const language = short === null || short === void 0 ? void 0 : short.language;
  const videoType = short === null || short === void 0 ? void 0 : short.type;
  const previewThumbnailRectangleUrl = enviro.isQa() && isAcceptanceTest() ? SHORTS_MOCK_TEST_THUMBNAIL : short === null || short === void 0 ? void 0 : short.previewThumbnailRectangleUrl;
  const shortVideoProps = Object.assign({}, videoProps, {
    creatorRegion: enviro.isQa() && isAcceptanceTest() ? na1 : videoProps === null || videoProps === void 0 ? void 0 : videoProps.creatorRegion
  });
  const renderLearnByDoingScreen = shouldRenderLearnByDoingScreen && previewThumbnailRectangleUrl && !isVideoFinished;
  const {
    isPracticeModeEnabled,
    setShowPracticeModeOverlay
  } = useIsPracticeModeEnabled(shortId);
  const [startShort] = useStartShort(shortId, language, playlistId);
  const {
    postRecommendationsContentEngagedFeedback
  } = usePostRecommendationsContentEngagedFeedback();
  const handleOnPlay = useCallback(() => {
    var _postRecommendationsC2;
    startShort().catch(Raven.captureException.bind(Raven));
    if (shortId) (_postRecommendationsC2 = postRecommendationsContentEngagedFeedback({
      item: {
        id: shortId,
        type: 'SHORT_FORM_CONTENT'
      },
      contentId: shortId
    })) === null || _postRecommendationsC2 === void 0 || _postRecommendationsC2.catch(Raven.captureException.bind(Raven));
  }, [postRecommendationsContentEngagedFeedback, shortId, startShort]);
  if (!videoProps || !shortId || !language) return null;
  if (shortLoading) return /*#__PURE__*/_jsx(LoadingOverlay, {
    contextual: true,
    layout: "centered",
    use: "spinner"
  });
  if (renderLearnByDoingScreen) {
    return /*#__PURE__*/_jsx(LearnByDoingCTAScreen, {
      backgroundImage: previewThumbnailRectangleUrl,
      otgId: shortId
    });
  }
  if (isPracticeModeEnabled) {
    return /*#__PURE__*/_jsx(PracticeModeOverlay, {
      hidePracticeMode: () => {
        setShowPracticeModeOverlay(false);
        setForceAutoPlayForPracticeMode(true);
      },
      otgId: shortId
    });
  }
  return /*#__PURE__*/_jsx(VideoPlayerBase, Object.assign({
    language: language,
    onPlay: handleOnPlay,
    shortId: shortId,
    shortName: shortName,
    videoType: videoType
  }, props, shortVideoProps, {
    autoPlay: shouldAutoPlay || forceAutoPlayForPracticeMode
  }), shortId);
};
export const VideoPlayer = _ref4 => {
  let {
      shortId
    } = _ref4,
    props = _objectWithoutPropertiesLoose(_ref4, _excluded4);
  if (shortId) return /*#__PURE__*/_jsx(VideoPlayerShort, Object.assign({
    shortId: shortId
  }, props));else return /*#__PURE__*/_jsx(VideoPlayerTrackLesson, Object.assign({}, props));
};
export default VideoPlayer;